<template>
  <div class="hospital" v-if="hospital">
    <Breadcrumbs :items="breadcrumbs">
      <rp-permissions :requireOne="['write_hospitals']">
        <router-link v-if="isAdmin || isHospitalOwner" :to="{ name: 'edit-hospital', params: { id: this.$route.params.id}}">
          <rp-sprite class="create-icon" name="create"/>
        </router-link>
      </rp-permissions>
    </Breadcrumbs>

    <div class="content">
      <div class="hospital-info">
        <div class="info-group">
          <h3>Name</h3>
          <p>{{hospital.name}}</p>
        </div>
        <div class="info-group">
          <h3>Country</h3>
          <p v-if="hospital.country">{{hospital.country.name}}</p>
        </div>
        <div class="info-group">
          <h3>City</h3>
          <p>{{hospital.town}}</p>
        </div>
        <div class="info-group">
          <h3>Address</h3>
          <div class="addresses">
            <p>{{hospital.address_1}}</p>
            <p>{{hospital.address_2}}</p>
          </div>
        </div>
        <div class="info-group">
          <h3>County</h3>
          <p>{{hospital.county}}</p>
        </div>
        <div class="info-group" v-if="isAdmin">
          <h3>Latitude</h3>
          <p>{{hospital.latitude}}</p>
        </div>
        <div class="info-group" v-if="isAdmin">
          <h3>Longitude</h3>
          <p>{{hospital.longitude}}</p>
        </div>
        <div class="info-group" v-if="hospital.logo_url">
          <h3>Logo</h3>
          <p><img v-bind:src="getHospitalLogoUrl()"></p>
        </div>
        <div class="table-container info-group" v-if="showData && hospital.surgeons && hospital.surgeons.length > 0">
          
          <h3>Surgeons:</h3>
          <rp-table
            name="users"
            noPagination
            :results="getSurgeons"
            :key="update"
            :headings="['name', 'email', 'gender', {heading: 'disassociate', key: 'disassociate', button: true}]"
            @rowClick="onSurgeonClick">
              <div
                v-for="(surgeon, index) in hospital['surgeons']"
                :key="index"
                :slot="'td-' + index + '-disassociate'">
                <span
                  @click.stop="activeUnlinkDelete(surgeon)">
                    <rp-sprite class="create-icon" name="disassociate"/>
                </span>
              </div>
          </rp-table>
        </div>
      </div>
    </div>
    <rp-overlay :active="activeUnlink">
      <rp-delete-confirm
        icon="warning"
        :messageText="disassociateConfirmText"
        :cancel="closeModal"
        :confirm="disassociateConfirmFunction"/>
    </rp-overlay>
  </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs'
import Status from 'raypro_ui/src/components/ui/Status'
import RpButton from 'raypro_ui/src/components/ui/inputs/Button'
import Table from 'raypro_ui/src/components/ui/Table'
import RpSprite from 'raypro_ui/src/components/ui/Sprite'
import {getHospital, getHospitalAdmin} from 'raypro_ui/src/api/hospitals'
import page from '@/mixins/page'
import {mapGetters} from 'vuex'
import Permissions from 'raypro_ui/src/components/Permissions'
import Overlay from 'raypro_ui/src/components/ui/Overlay'
import DeleteConfirm from 'raypro_ui/src/components/ui/DeleteConfirm'
import {removeHospitalFromSurgeon} from 'raypro_ui/src/api/users'


export default {
  components: {
    Breadcrumbs,
    Status,
    RpButton,
    'rp-table': Table,
    RpSprite,
    'rp-permissions': Permissions,
    'rp-overlay': Overlay,
    'rp-delete-confirm': DeleteConfirm
  },

  mixins: [page],

  data() {
    return {
      hospital: {},
      showData: false,
      title: 'Hospital',
      activeUnlink: false,
      disassociateConfirmText: '',
      disassociateConfirmFunction: null,
      disassociateObject: null,
      update: 0
    }
  },

  mounted() {
    this.getHospitalData()
  },

  watch: {
    id: function() {
      this.getHospitalData()
    }
  },

  methods: {
    getHospitalData() {
      let getHospitalDataFunction

      if(this.isAdmin) {
        getHospitalDataFunction = () => { return getHospitalAdmin(this.id) }
      } else {
        getHospitalDataFunction = () => { return getHospital(this.id) }
      }

      getHospitalDataFunction().then(({data}) => {
        data.latitude = data.latlng && data.latlng[0]
        data.longitude = data.latlng && data.latlng[1]
        this.hospital = data
        this.showData = true
        this.update++
      })
    },

    getHospitalLogoUrl() {
      return this.hospital.logo_url + `?cachebust=${new Date().getTime()}`
    },

    activeUnlinkDelete(obj) {
      this.disassociateConfirmText = `Unassociate surgeon ${obj.profile.first_name} ${obj.profile.last_name}`
      this.disassociateObject = obj
      this.disassociateConfirmFunction = this.disassociateHospital
      this.activeUnlink = !this.activeUnlink
    },

    closeModal() {
      this.activeUnlink = !this.activeUnlink
    },

    disassociateHospital() {
      const payload = {
        userId: this.disassociateObject.id,
        hospitalId: this.hospital.id
      }

      removeHospitalFromSurgeon(payload).then(() => {
        this.getHospitalData()
        this.closeModal()
      })
    },

    getSurgeons() {
      return new Promise((resolve) => {
        const mapped = this.hospital.surgeons.map((row) => {
          return {
            id: row.id,
            name: `${row.profile.first_name} ${row.profile.last_name}`,
            email: row.email,
            // TODO: map to age group
            dob: row.profile.dob,
            gender: row.profile.gender,
            role: row.roles[0].name
          }
        })

        resolve({
          data: mapped,
          page
        })
      })
    },

    onSurgeonClick(row) {
      this.$router.push(`/surgeons/${row.id}`)
    }
  },

  computed: {
    ...mapGetters(['authUser']),

    isAdmin() {
      return this.authUser && this.authUser.roles.map((el) => { return el.name }).indexOf('raypro_admin') !== -1
    },

    breadcrumbs() {
      return [
        {
          label: 'All Hospitals',
          link: 'hospitals'
        },
        {label: this.hospital.name}
      ]
    },

    isHospitalOwner() {
      return this.hospital && this.authUser.id === this.hospital.created_by
    }
  },

  props: {id: String}
}
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'
  
  .content
    @include media($media-medium)
      padding: 0 40px
    .hospital-info
      width: 100%
      .info-group
        display: flex
        flex-direction: column
        margin-top: 20px
        @include media($media-medium)
          flex-direction: row
          align-items: flex-start
          width: 40%
        h3
          display: inline-flex
          width: 150px
          min-width: 150px
          margin: 0
          text-transform: initial
          font-weight: lighter
        p
          display: inline-flex
          line-height: 1
          font-weight: bold  
          word-break: break-word
          @include media($media-medium)
            margin-top: 0
        .addresses
          p
            display: block
      .table-container
          flex-direction: column
          align-items: flex-start
          width: 100%
          h3
            margin: 0 0 15px 0
          .table
            margin-bottom: 15px
            width: 100%
</style>



// WEBPACK FOOTER //
// src/components/pages/Hospitals/Hospital.vue